"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Re-resolves `window.location.search` whenever a `popstate` event fires.
 *
 * Why not `useSearchParams()` from `next/navigation`? It does not observe
 * `window.history.replaceState` writes — which is exactly how the search
 * filter widgets (via `useHandleSearchParams`) and `useCustomResultsPage`'s
 * own clear handlers update the URL. Subscribing to `popstate` (which we
 * dispatch synthetically after every `replaceState`) lets this component
 * forward URL changes to `DynamicFlowWrapper` so the DE flow refetches.
 *
 * SSR / first-paint behavior is preserved by reading the caller-provided
 * `initialString` (typically the `queryString` SSR prop) until mount.
 * The mount read keeps that fallback when the address bar has no query, so a
 * CMS-authored search string is not cleared. A later popstate can still
 * resolve to an empty string when the visitor clears the URL.
 */
const useUrlSearchString = (initialString: string): string => {
  const initialStringRef = useRef(initialString);
  const [searchString, setSearchString] = useState<string>(() => {
    if (typeof window === "undefined") return initialString;
    return window.location.search.replace(/^\?/, "") || initialString;
  });

  useEffect(() => {
    const read = () => window.location.search.replace(/^\?/, "");
    setSearchString(read() || initialStringRef.current);

    const handlePopState = () => setSearchString(read());
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return searchString;
};

export default useUrlSearchString;
